/**
 * ProspectsView — Phase E2 Cold Outreach Workspace.
 *
 * Lists researched cold prospects from the prospector pipeline.
 * - Top: provider-status banner (probe of Perplexity / Regrid / Apollo).
 * - KPI tiles: total + by-status counts.
 * - Filters: audience, status, search, min score.
 * - Click row → drawer with research summary, citations, facts, draft email + call script.
 * - "Research now" form to manually trigger a research batch.
 * Nothing is auto-sent. Promotion to a cold sequence happens explicitly in E.2.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CalendarPlus,
  CheckCircle2,
  Crosshair,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  ShieldX,
  Sparkles,
  Target,
  Trash2,
  Zap,
  CheckSquare,
  Square,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { crmApi } from "@/lib/ownerClient";
import { NewAppointmentDialog } from "@/components/owner/CalendarView";

const AUDIENCES = [
  { value: "all", label: "All audiences" },
  { value: "residential", label: "Residential" },
  { value: "hoa", label: "HOA / Property Mgmt" },
  { value: "realtor", label: "Realtors" },
  { value: "investor", label: "Investors" },
];

const STATUS_OPTS = [
  { value: "all", label: "All statuses" },
  { value: "draft", label: "Draft" },
  { value: "researching", label: "Researching" },
  { value: "researched", label: "Researched" },
  { value: "promoted", label: "Promoted" },
  { value: "responded", label: "Responded" },
  { value: "converted", label: "Converted" },
  { value: "suppressed", label: "Suppressed" },
  { value: "failed", label: "Failed" },
];

const STATUS_STYLES = {
  draft: "border-zinc-700 text-zinc-300 bg-zinc-800/40",
  researching: "border-blue-500/40 text-blue-300 bg-blue-500/10",
  researched: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  promoted: "border-violet-500/40 text-violet-300 bg-violet-500/10",
  responded: "border-cyan-500/40 text-cyan-300 bg-cyan-500/10",
  converted: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  suppressed: "border-zinc-600 text-zinc-500 bg-zinc-800/40",
  failed: "border-red-500/40 text-red-300 bg-red-500/10",
};

const AUDIENCE_LABEL = {
  residential: "Residential",
  hoa: "HOA",
  realtor: "Realtor",
  investor: "Investor",
  commercial: "Commercial",
};

function ProviderPill({ name, status }) {
  if (!status) {
    return (
      <Badge variant="outline" className="border-zinc-800 text-zinc-500 text-[10px]">
        {name}: unknown
      </Badge>
    );
  }
  const ok = status.ok;
  const cls = ok
    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
    : "border-amber-500/40 text-amber-300 bg-amber-500/10";
  const detail = ok
    ? `${status.latency_ms || "?"}ms`
    : status.status
      ? `${status.status}`
      : status.reason
        ? "error"
        : "not configured";
  return (
    <Badge
      variant="outline"
      className={`text-[10px] ${cls}`}
      data-testid={`provider-${name.toLowerCase()}-status`}
    >
      {ok ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <AlertCircle className="h-3 w-3 mr-1" />}
      {name}: {ok ? "live" : detail}
    </Badge>
  );
}

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.draft;
  return (
    <Badge
      variant="outline"
      className={`text-[10px] uppercase tracking-[0.14em] ${cls}`}
      data-testid={`prospect-status-${status || "draft"}`}
    >
      {status || "draft"}
    </Badge>
  );
}

const AUDIENCE_TO_SEQUENCE = {
  residential: "cold_outreach_residential",
  hoa: "cold_outreach_hoa",
  realtor: "cold_outreach_realtor",
  investor: "cold_outreach_investor",
  commercial: "cold_outreach_hoa",
};

const SEQUENCE_OPTS = [
  { value: "cold_outreach_residential", label: "Residential — 3-touch cold email" },
  { value: "cold_outreach_hoa", label: "HOA / Property Mgmt — 3-touch" },
  { value: "cold_outreach_realtor", label: "Realtor partner — 3-touch" },
  { value: "cold_outreach_investor", label: "Investor — 3-touch" },
];

function GateBlock({ gate }) {
  if (!gate) return null;
  const ok = gate.ok;
  return (
    <div
      data-testid="dana-gate-result"
      className={`rounded-md border p-3 text-[12.5px] space-y-1.5 ${
        ok
          ? "border-emerald-500/40 bg-emerald-500/5"
          : "border-amber-500/40 bg-amber-500/5"
      }`}
    >
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.16em]">
        {ok ? (
          <ShieldCheck className="h-4 w-4 text-emerald-300" />
        ) : (
          <ShieldX className="h-4 w-4 text-amber-300" />
        )}
        <span className={ok ? "text-emerald-200" : "text-amber-200"}>
          Dana CAN-SPAM gate: {ok ? "PASS" : "BLOCK"}
        </span>
        {gate.suppressed ? (
          <Badge variant="outline" className="border-red-500/40 text-red-300 text-[10px]">
            On suppression list
          </Badge>
        ) : null}
      </div>
      {(gate.violations || []).length > 0 && (
        <ul className="space-y-1">
          {gate.violations.map((v, idx) => (
            <li key={`v-${idx}`} className="text-red-300 flex gap-2">
              <span className="mt-0.5">✗</span>
              <span>{v}</span>
            </li>
          ))}
        </ul>
      )}
      {(gate.warnings || []).length > 0 && (
        <ul className="space-y-1">
          {gate.warnings.map((w, idx) => (
            <li key={`w-${idx}`} className="text-amber-200 flex gap-2">
              <span className="mt-0.5">⚠</span>
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}
      {(gate.notes || []).length > 0 && (
        <ul className="space-y-1 pt-1 border-t border-zinc-800/50">
          {gate.notes.map((n, idx) => (
            <li key={`n-${idx}`} className="text-zinc-400 text-[11px] flex gap-2">
              <span className="mt-0.5">·</span>
              <span>{n}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PromoteDialog({ open, onOpenChange, prospect, onPromoted }) {
  const initialSeq =
    AUDIENCE_TO_SEQUENCE[prospect?.audience] || "cold_outreach_residential";
  const [sequenceSlug, setSequenceSlug] = useState(initialSeq);
  const [subject, setSubject] = useState(prospect?.draft_subject || "");
  const [body, setBody] = useState(prospect?.draft_body || "");
  const [gate, setGate] = useState(null);
  const [checking, setChecking] = useState(false);
  const [promoting, setPromoting] = useState(false);

  // When the dialog opens for a new prospect, reset state.
  useEffect(() => {
    if (open && prospect) {
      setSequenceSlug(AUDIENCE_TO_SEQUENCE[prospect.audience] || "cold_outreach_residential");
      setSubject(prospect.draft_subject || "");
      setBody(prospect.draft_body || "");
      setGate(null);
    }
  }, [open, prospect]);

  const primaryEmail = useMemo(() => {
    const c = (prospect?.contacts || []).find((x) => x.is_primary) || (prospect?.contacts || [])[0];
    return (c?.email || "").trim();
  }, [prospect]);

  async function runCheck() {
    setChecking(true);
    try {
      const result = await crmApi.danaCheck({
        subject,
        body,
        recipient_email: primaryEmail,
      });
      setGate(result);
      if (result.ok) toast.success("Dana gate: PASS");
      else toast.warning("Dana gate: BLOCK — fix violations before promote");
    } catch (err) {
      toast.error(err.message || "Dana check failed");
    } finally {
      setChecking(false);
    }
  }

  async function doPromote() {
    if (!prospect?.id) return;
    setPromoting(true);
    try {
      const result = await crmApi.promoteProspect(prospect.id, {
        sequence_slug: sequenceSlug,
        override_subject: subject,
        override_body: body,
      });
      if (result.ok) {
        toast.success(`Promoted into ${result.sequence_slug}`);
        onPromoted?.(result);
        onOpenChange(false);
      } else {
        setGate(result.gate || null);
        toast.error(result.reason || "Promotion blocked by Dana");
      }
    } catch (err) {
      toast.error(err.message || "Promote failed");
    } finally {
      setPromoting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="dark max-w-[680px] bg-black border-zinc-800 text-white overflow-y-auto max-h-[90vh]"
        data-testid="promote-prospect-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">
            Promote to cold sequence
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Dana will run the CAN-SPAM gate and suppression check before enrolling. Nothing sends until enrollment ticks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Recipient */}
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 text-[12.5px]">
            <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
              Recipient
            </div>
            <div className="text-zinc-200">
              {prospect?.org_name || prospect?.address || "(unnamed prospect)"}
              {primaryEmail ? (
                <span className="text-zinc-400"> · {primaryEmail}</span>
              ) : (
                <span className="ml-2 text-amber-300">no contact email on file</span>
              )}
            </div>
          </div>

          {/* Sequence picker */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Sequence
            </Label>
            <Select value={sequenceSlug} onValueChange={setSequenceSlug}>
              <SelectTrigger
                className="h-9 bg-zinc-950 border-zinc-800 text-white"
                data-testid="promote-sequence-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEQUENCE_OPTS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Subject (editable) */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Subject
            </Label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="h-9 bg-zinc-950 border-zinc-800 text-white"
              data-testid="promote-subject"
            />
          </div>

          {/* Body (editable) */}
          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Body (CAN-SPAM footer auto-injected on send)
            </Label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[180px] font-mono text-[12.5px] bg-zinc-950 border-zinc-800 text-white"
              data-testid="promote-body"
            />
          </div>

          {/* Dana gate */}
          <GateBlock gate={gate} />
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={runCheck}
            disabled={checking}
            className="border-zinc-700 text-white hover:bg-zinc-900"
            data-testid="promote-run-check"
          >
            {checking ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <ShieldCheck className="h-4 w-4 mr-1.5" />}
            Run Dana check
          </Button>
          <Button
            type="button"
            onClick={doPromote}
            disabled={promoting}
            className="bg-violet-500 hover:bg-violet-400 text-black font-semibold"
            data-testid="promote-confirm"
          >
            {promoting ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />}
            Promote &amp; enroll
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const SOURCE_LABELS = {
  permits_austin: { label: "Austin permits (free)", color: "border-emerald-500/40 text-emerald-200" },
  perplexity_storm: { label: "Storm news (Perplexity)", color: "border-amber-500/40 text-amber-200" },
  regrid_fuzzy: { label: "Regrid fuzzy", color: "border-zinc-700 text-zinc-400" },
  traviscad_local: { label: "TravisCAD local", color: "border-violet-500/40 text-violet-200" },
};

const ALL_SOURCES = ["permits_austin", "perplexity_storm", "regrid_fuzzy", "traviscad_local"];

function ProspectorPanel() {
  const [status, setStatus] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ticking, setTicking] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const snap = await crmApi.prospectorStatus();
      setStatus(snap);
      setSettings(snap?.settings || null);
    } catch (err) {
      toast.error(err.message || "Failed to load prospector status");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
    const interval = setInterval(reload, 30000); // soft refresh every 30s
    return () => clearInterval(interval);
  }, [reload]);

  async function patchSettings(updates) {
    setSaving(true);
    try {
      const next = await crmApi.updateProspectorSettings(updates);
      setSettings(next);
      // Refresh status to recompute mode
      await reload();
      toast.success("Settings updated");
    } catch (err) {
      toast.error(err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  async function forceTick() {
    setTicking(true);
    try {
      const res = await crmApi.forceProspectorTick();
      const inserted = res?.discoveries || 0;
      const enriched = res?.enrichments || 0;
      toast.success(
        `Tick: ${res?.mode} mode — ${inserted} discovered, ${enriched} enriched`,
      );
      await reload();
    } catch (err) {
      toast.error(err.message || "Tick failed");
    } finally {
      setTicking(false);
    }
  }

  function toggleSource(slug) {
    if (!settings) return;
    const cur = Array.from(settings.enabled_sources || []);
    const next = cur.includes(slug)
      ? cur.filter((s) => s !== slug)
      : [...cur, slug];
    patchSettings({ enabled_sources: next });
  }

  if (loading && !status) {
    return (
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-4 text-zinc-500 text-[12px] flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading prospector…
        </CardContent>
      </Card>
    );
  }

  const today = status?.today || {};
  const trailingHour = status?.trailing_hour_api || {};
  const queue = status?.queue_counts || {};
  const mode = status?.mode || "paused";
  const enabledSources = settings?.enabled_sources || [];

  const modeColor =
    mode === "active"
      ? "border-emerald-500/40 text-emerald-200"
      : mode === "quiet"
      ? "border-amber-500/40 text-amber-200"
      : "border-zinc-700 text-zinc-400";

  return (
    <Card
      className="bg-zinc-950 border-zinc-800"
      data-testid="prospector-panel"
    >
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Crosshair className="h-3.5 w-3.5 text-amber-300" />
            <span className="text-[11px] uppercase tracking-[0.16em] text-zinc-400">
              24/7 Prospector — Alex
            </span>
            <Badge variant="outline" className={modeColor}>
              {mode}
            </Badge>
            {settings?.enabled ? (
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-300 text-[10px]">
                ON
              </Badge>
            ) : (
              <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-[10px]">
                OFF
              </Badge>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={forceTick}
              disabled={ticking}
              className="border-zinc-700 text-zinc-200 hover:bg-zinc-900 h-8"
              data-testid="prospector-force-tick"
            >
              {ticking ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Zap className="h-3.5 w-3.5 mr-1.5" />}
              Force tick
            </Button>
            <Button
              size="sm"
              onClick={() => patchSettings({ enabled: !settings?.enabled })}
              disabled={saving}
              className={
                settings?.enabled
                  ? "bg-zinc-800 hover:bg-zinc-700 text-white h-8"
                  : "bg-amber-500 hover:bg-amber-400 text-black font-semibold h-8"
              }
              data-testid="prospector-toggle"
            >
              {settings?.enabled ? "Pause" : "Turn ON"}
            </Button>
          </div>
        </div>

        {/* Stats grid */}
        <div className="grid sm:grid-cols-3 gap-2 text-[12px]">
          <div className="rounded-md border border-zinc-800 bg-black p-2.5">
            <div className="uppercase tracking-[0.14em] text-[10px] text-zinc-500">
              Today
            </div>
            <div className="text-white mt-0.5">
              {today.discoveries || 0} discovered · {today.enrichments || 0} enriched
            </div>
            <div className="text-[10.5px] text-zinc-500">
              {today.ticks || 0} ticks · {today.dismissed || 0} below-score dismissed
            </div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-black p-2.5">
            <div className="uppercase tracking-[0.14em] text-[10px] text-zinc-500">
              Queue
            </div>
            <div className="text-white mt-0.5">
              {(queue.discovered || 0) + (queue.researched || 0)} ready
            </div>
            <div className="text-[10.5px] text-zinc-500">
              {queue.discovered || 0} pending enrichment · {queue.researched || 0} ready-to-promote
            </div>
          </div>
          <div className="rounded-md border border-zinc-800 bg-black p-2.5">
            <div className="uppercase tracking-[0.14em] text-[10px] text-zinc-500">
              Trailing hour API
            </div>
            <div className="text-white mt-0.5">
              Regrid {trailingHour.regrid || 0}/{settings?.regrid_calls_per_hour_cap || 60} · Pplx {trailingHour.perplexity || 0}/{settings?.perplexity_calls_per_hour_cap || 30}
            </div>
            <div className="text-[10.5px] text-zinc-500">
              local {status?.local_hour ?? "?"}:00 ({settings?.timezone || "America/Chicago"})
            </div>
          </div>
        </div>

        {/* Source toggles */}
        <div className="space-y-1.5">
          <div className="uppercase tracking-[0.14em] text-[10px] text-zinc-500">
            Discovery channels (rotate every tick)
          </div>
          <div className="flex flex-wrap gap-2">
            {ALL_SOURCES.map((slug) => {
              const meta = SOURCE_LABELS[slug] || { label: slug, color: "border-zinc-700 text-zinc-400" };
              const on = enabledSources.includes(slug);
              return (
                <button
                  key={slug}
                  type="button"
                  onClick={() => toggleSource(slug)}
                  disabled={saving}
                  className={`px-2.5 py-1 rounded text-[11px] border transition-colors ${
                    on
                      ? meta.color + " bg-zinc-950"
                      : "border-zinc-800 text-zinc-600 hover:text-zinc-400"
                  }`}
                  data-testid={`prospector-source-${slug}`}
                >
                  {on ? "✓ " : ""}
                  {meta.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Per-source counts from the most recent tick */}
        {status?.last_tick?.by_source ? (
          <details className="text-[11.5px]">
            <summary className="cursor-pointer text-[10px] uppercase tracking-[0.14em] text-zinc-500 hover:text-zinc-300">
              Last tick → {new Date(status.last_tick.started_at).toLocaleTimeString()} ({status.last_tick.mode})
            </summary>
            <div className="mt-1.5 grid sm:grid-cols-2 gap-1.5">
              {Object.entries(status.last_tick.by_source).map(([src, s]) => (
                <div key={src} className="rounded border border-zinc-800 bg-black p-2">
                  <div className="text-white">{SOURCE_LABELS[src]?.label || src}</div>
                  <div className="text-[10.5px] text-zinc-500">
                    inserted {s.inserted || 0} · found {s.found || 0} · dupe {s.skipped_dupes || 0}
                    {s.error ? <span className="text-red-300"> · {s.error.slice(0, 80)}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          </details>
        ) : null}

        {/* Dial sliders */}
        <details className="text-[12px]">
          <summary className="cursor-pointer text-[10px] uppercase tracking-[0.14em] text-zinc-500 hover:text-zinc-300">
            Tuning dials
          </summary>
          <div className="mt-2 grid sm:grid-cols-3 gap-2">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5 block">
                Target / day
              </Label>
              <Input
                type="number"
                value={settings?.target_daily_count ?? 150}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, target_daily_count: Number(e.target.value) || 0 }))
                }
                onBlur={() => patchSettings({ target_daily_count: settings?.target_daily_count })}
                className="h-8 bg-black border-zinc-800 text-white"
                data-testid="prospector-target-daily"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5 block">
                Min score
              </Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={settings?.min_score ?? 70}
                onChange={(e) =>
                  setSettings((s) => ({ ...s, min_score: Number(e.target.value) || 0 }))
                }
                onBlur={() => patchSettings({ min_score: settings?.min_score })}
                className="h-8 bg-black border-zinc-800 text-white"
                data-testid="prospector-min-score"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.14em] text-zinc-500 mb-1.5 block">
                Quiet hours (local)
              </Label>
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  min={0} max={23}
                  value={settings?.quiet_hour_start_local ?? 1}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, quiet_hour_start_local: Number(e.target.value) || 0 }))
                  }
                  onBlur={() => patchSettings({ quiet_hour_start_local: settings?.quiet_hour_start_local })}
                  className="h-8 bg-black border-zinc-800 text-white w-16"
                  data-testid="prospector-quiet-start"
                />
                <span className="text-zinc-500">→</span>
                <Input
                  type="number"
                  min={0} max={23}
                  value={settings?.quiet_hour_end_local ?? 5}
                  onChange={(e) =>
                    setSettings((s) => ({ ...s, quiet_hour_end_local: Number(e.target.value) || 0 }))
                  }
                  onBlur={() => patchSettings({ quiet_hour_end_local: settings?.quiet_hour_end_local })}
                  className="h-8 bg-black border-zinc-800 text-white w-16"
                  data-testid="prospector-quiet-end"
                />
              </div>
            </div>
          </div>
        </details>
      </CardContent>
    </Card>
  );
}


function SuppressionsPanel({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [reasonInput, setReasonInput] = useState("manual");
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        crmApi.listSuppressions({ limit: 50 }),
        crmApi.suppressionsSummary(),
      ]);
      setItems(list.suppressions || []);
      setSummary(sum);
    } catch (err) {
      toast.error(err.message || "Failed to load suppressions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  async function addManual() {
    if (!emailInput.trim()) return;
    setAdding(true);
    try {
      await crmApi.addSuppression({
        email: emailInput.trim(),
        reason: reasonInput,
        notes: "Manually added via Owner CRM",
      });
      setEmailInput("");
      toast.success("Suppressed");
      load();
    } catch (err) {
      toast.error(err.message || "Failed to add");
    } finally {
      setAdding(false);
    }
  }

  async function removeOne(email) {
    try {
      await crmApi.removeSuppression(email);
      toast.success(`Unsuppressed ${email}`);
      load();
    } catch (err) {
      toast.error(err.message || "Failed to remove");
    }
  }

  return (
    <Card className="bg-zinc-950 border-zinc-800" data-testid="suppressions-panel">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2">
            <ShieldX className="h-3 w-3" />
            Suppression list
          </div>
          {summary ? (
            <div className="text-[11px] text-zinc-500">
              {summary.total} suppressed · opt-out {summary.by_reason?.opt_out ?? 0} · spam {summary.by_reason?.spam_report ?? 0} · hard-bounce {summary.by_reason?.bounce_hard ?? 0}
            </div>
          ) : null}
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-2">
          <div className="flex-1 min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Add email to suppression list
            </Label>
            <Input
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              placeholder="someone@example.com"
              className="h-9 bg-black border-zinc-800 text-white"
              data-testid="suppress-email-input"
            />
          </div>
          <div className="w-[180px]">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Reason
            </Label>
            <Select value={reasonInput} onValueChange={setReasonInput}>
              <SelectTrigger className="h-9 bg-black border-zinc-800 text-white" data-testid="suppress-reason-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="opt_out">Opt-out</SelectItem>
                <SelectItem value="complaint">Complaint</SelectItem>
                <SelectItem value="dnc">DNC</SelectItem>
                <SelectItem value="bounce_hard">Hard bounce</SelectItem>
                <SelectItem value="spam_report">Spam report</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={addManual}
            disabled={adding || !emailInput.trim()}
            className="h-9 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            data-testid="suppress-add"
          >
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}
          </Button>
        </div>

        <div className="border-t border-zinc-800 pt-2">
          {loading ? (
            <div className="text-zinc-500 text-[12px] flex items-center gap-2 py-3">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="text-zinc-500 text-[12px] py-3">No suppressions yet.</div>
          ) : (
            <div className="max-h-[240px] overflow-y-auto" data-testid="suppressions-list">
              {items.map((s) => (
                <div
                  key={s.id || s.email}
                  className="flex items-center justify-between py-1.5 text-[12.5px] border-b border-zinc-900 last:border-b-0"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-white truncate">{s.email}</div>
                    <div className="text-[10.5px] text-zinc-500">
                      {s.reason} · {s.source}{s.notes ? ` · ${s.notes}` : ""}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeOne(s.email)}
                    className="text-zinc-400 hover:text-red-300 h-7 px-2"
                    data-testid={`suppress-remove-${s.email}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ProspectDetailDrawer({ open, prospectId, onClose, onUpdated }) {
  const [prospect, setProspect] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [statusDraft, setStatusDraft] = useState("");
  const [notesDraft, setNotesDraft] = useState("");
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [suppressing, setSuppressing] = useState(false);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!open) {
        if (active) setProspect(null);
        return;
      }
      if (!prospectId) return;
      setLoading(true);
      try {
        const data = await crmApi.getProspect(prospectId);
        if (!active) return;
        setProspect(data);
        setStatusDraft(data?.status || "researched");
        setNotesDraft(data?.notes || "");
      } catch (err) {
        if (active) toast.error(err.message || "Failed to load prospect");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, prospectId]);

  async function handleSave() {
    if (!prospectId) return;
    setSaving(true);
    try {
      const updated = await crmApi.updateProspect(prospectId, {
        status: statusDraft,
        notes: notesDraft,
      });
      toast.success("Prospect updated");
      setProspect(updated);
      onUpdated?.();
    } catch (err) {
      toast.error(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  async function handleSuppress() {
    if (!prospectId) return;
    setSuppressing(true);
    try {
      const result = await crmApi.suppressProspect(prospectId, {
        reason: "manual",
        notes: "Suppressed via prospect detail",
      });
      toast.success(
        result?.email
          ? `Suppressed (${result.email} added to suppression list)`
          : "Suppressed",
      );
      setProspect(result?.prospect || null);
      setStatusDraft("suppressed");
      onUpdated?.();
    } catch (err) {
      toast.error(err.message || "Suppress failed");
    } finally {
      setSuppressing(false);
    }
  }

  async function quickAction(status) {
    if (!prospectId) return;
    try {
      const updated = await crmApi.updateProspect(prospectId, { status });
      setProspect(updated);
      setStatusDraft(status);
      toast.success(`Marked ${status}`);
      onUpdated?.();
    } catch (err) {
      toast.error(err.message || "Update failed");
    }
  }

  const contacts = prospect?.contacts || [];
  const facts = prospect?.research_facts || [];
  const citations = prospect?.research_citations || [];

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent
        side="right"
        className="dark w-full sm:max-w-[640px] bg-black text-white border-l border-zinc-800 overflow-y-auto"
        data-testid="prospect-detail-drawer"
      >
        <SheetHeader>
          <SheetTitle className="text-white">
            {prospect?.org_name || prospect?.address || "Prospect"}
          </SheetTitle>
          <SheetDescription className="text-zinc-400">
            {prospect?.audience ? AUDIENCE_LABEL[prospect.audience] || prospect.audience : ""}
            {prospect?.zip_code ? ` · ${prospect.zip_code}` : ""}
            {prospect?.score !== undefined ? ` · score ${prospect.score}` : ""}
          </SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="flex items-center justify-center py-10 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        )}

        {prospect && !loading && (
          <div className="mt-4 space-y-5">
            {/* Quick actions */}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                onClick={() => setPromoteOpen(true)}
                disabled={prospect.status === "promoted" || prospect.status === "suppressed"}
                className="bg-violet-500 hover:bg-violet-400 text-black font-semibold disabled:opacity-40"
                data-testid="prospect-promote"
              >
                <Zap className="h-3.5 w-3.5 mr-1.5" />
                Promote → cold sequence
              </Button>
              <Button
                size="sm"
                onClick={() => setScheduleOpen(true)}
                disabled={prospect.status === "suppressed"}
                className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold disabled:opacity-40"
                data-testid="prospect-schedule-consult"
              >
                <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />
                Schedule consult
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleSuppress}
                disabled={suppressing || prospect.status === "suppressed"}
                className="border-zinc-700 text-zinc-200 hover:bg-zinc-900"
                data-testid="prospect-suppress"
              >
                {suppressing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ShieldX className="h-3.5 w-3.5 mr-1.5" />}
                Suppress
              </Button>
              {prospect.status === "promoted" && prospect.enrolled_sequence_slug ? (
                <Badge variant="outline" className="border-violet-500/40 text-violet-200 text-[10px]">
                  Enrolled in {prospect.enrolled_sequence_slug}
                </Badge>
              ) : null}
            </div>

            {/* Stats grid */}
            <div className="grid grid-cols-2 gap-3 text-[12px]">
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="uppercase tracking-[0.16em] text-[10px] text-zinc-500 mb-1">
                  Recommended offer
                </div>
                <div className="text-white">{prospect.recommended_offer || "—"}</div>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="uppercase tracking-[0.16em] text-[10px] text-zinc-500 mb-1">
                  Score
                </div>
                <div className="text-white">{prospect.score}/100</div>
              </div>
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 col-span-2">
                <div className="uppercase tracking-[0.16em] text-[10px] text-zinc-500 mb-1">
                  Address / org
                </div>
                <div className="text-white break-all">
                  {prospect.org_name || prospect.address || "—"}
                </div>
              </div>
            </div>

            {/* Score reasons */}
            {Array.isArray(prospect.score_reasons) && prospect.score_reasons.length > 0 && (
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-1.5">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 flex items-center gap-1.5">
                  <Sparkles className="h-3 w-3" /> Score reasoning
                </div>
                {prospect.score_reasons.map((r, idx) => (
                  <div key={idx} className="text-[12.5px] text-zinc-300 flex items-start gap-2">
                    <span className="text-amber-400 mt-0.5">•</span>
                    <span>{r}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Research summary */}
            <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
              <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">
                Research summary
              </div>
              <div className="text-[13px] text-zinc-200 whitespace-pre-wrap leading-relaxed">
                {prospect.research_summary || "(no summary)"}
              </div>
            </div>

            {/* Contacts */}
            {contacts.length > 0 && (
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">
                  Contacts ({contacts.length})
                </div>
                {contacts.map((c, idx) => (
                  <div key={idx} className="text-[12.5px] text-zinc-200">
                    <span className="font-medium">{c.name || "(unnamed)"}</span>
                    {c.title ? <span className="text-zinc-500"> · {c.title}</span> : null}
                    {c.email ? <span className="text-zinc-400"> · {c.email}</span> : null}
                    {c.dnc_status === "on_dnc" ? (
                      <Badge variant="outline" className="ml-2 border-red-500/40 text-red-300 text-[10px]">
                        DNC
                      </Badge>
                    ) : null}
                  </div>
                ))}
              </div>
            )}

            {/* Research facts */}
            {facts.length > 0 && (
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-2">
                  Research facts
                </div>
                <div className="space-y-1.5">
                  {facts.map((f, idx) => (
                    <div key={idx} className="text-[12.5px]">
                      <span className="text-zinc-500 mr-1.5">{f.label}:</span>
                      <span className="text-zinc-200">{f.value}</span>
                      <span className="text-zinc-600 ml-1.5">({f.source})</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Citations */}
            {citations.length > 0 && (
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5">
                  Citations ({citations.length})
                </div>
                <ul className="space-y-1">
                  {citations.map((url, idx) => (
                    <li key={idx} className="text-[11.5px] text-amber-300 break-all">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        [{idx + 1}] {url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Draft email */}
            {(prospect.draft_subject || prospect.draft_body) && (
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                  Draft cold email (review before promote)
                </div>
                <div className="text-[12px] text-zinc-300">
                  <span className="text-zinc-500">Subject:</span> {prospect.draft_subject}
                </div>
                <div className="text-[12.5px] text-zinc-200 whitespace-pre-wrap bg-black border border-zinc-900 p-3 rounded">
                  {prospect.draft_body}
                </div>
              </div>
            )}

            {/* Draft call script */}
            {prospect.draft_call_script && (
              <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
                  Draft call script (queue only — no autodial)
                </div>
                <div className="text-[12.5px] text-zinc-200 whitespace-pre-wrap bg-black border border-zinc-900 p-3 rounded">
                  {prospect.draft_call_script}
                </div>
              </div>
            )}

            {/* Status + notes editor */}
            <div className="space-y-3 rounded-md border border-zinc-800 bg-zinc-950 p-3">
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
                  Status
                </Label>
                <Select value={statusDraft} onValueChange={setStatusDraft}>
                  <SelectTrigger
                    className="h-9 bg-black border-zinc-800 text-white"
                    data-testid="prospect-status-select"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTS.filter((o) => o.value !== "all").map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
                  Notes
                </Label>
                <Textarea
                  value={notesDraft}
                  onChange={(e) => setNotesDraft(e.target.value)}
                  placeholder="Internal notes…"
                  data-testid="prospect-notes"
                  className="min-h-[80px] bg-black border-zinc-800 text-white placeholder:text-zinc-600"
                />
              </div>
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-amber-500 hover:bg-amber-400 text-black font-semibold"
                data-testid="prospect-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
              </Button>
            </div>
          </div>
        )}

        <PromoteDialog
          open={promoteOpen}
          onOpenChange={setPromoteOpen}
          prospect={prospect}
          onPromoted={() => {
            onUpdated?.();
          }}
        />
        <NewAppointmentDialog
          open={scheduleOpen}
          onOpenChange={setScheduleOpen}
          prospectId={prospect?.id || null}
          initialContact={{
            contact_name:
              prospect?.contacts?.[0]?.name ||
              prospect?.org_name ||
              "",
            contact_email:
              prospect?.contacts?.[0]?.email || "",
            contact_phone:
              prospect?.contacts?.[0]?.phone || "",
            address: prospect?.address || "",
            title: "Black Ridge consult / walk-through",
            notes: prospect?.research_summary?.slice(0, 400) || "",
          }}
          onCreated={() => {
            toast.success("Consult scheduled \u2014 prospect emailed the invite");
            onUpdated?.();
          }}
        />
      </SheetContent>
    </Sheet>
  );
}

function ResearchPanel({ providerStatus, onCompleted }) {
  const [audience, setAudience] = useState("residential");
  const [count, setCount] = useState(5);
  const [running, setRunning] = useState(false);

  const providers = providerStatus?.providers || {};
  const b2bOk = useMemo(() => {
    // Dual-source: either Apollo OR Perplexity B2B is live.
    return Boolean(
      providers.apollo?.ok || providers.perplexity_b2b?.ok,
    );
  }, [providers]);
  const audienceProviderOk = useMemo(() => {
    if (audience === "residential") {
      return Boolean(providers.regrid?.ok);
    }
    return b2bOk;
  }, [audience, providers, b2bOk]);

  async function handleRun() {
    setRunning(true);
    try {
      const result = await crmApi.runProspectResearch({ audience, count: Number(count) });
      toast.success(`Created ${result.created || 0} prospects`);
      onCompleted?.();
    } catch (err) {
      toast.error(err.message || "Research failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card className="bg-zinc-950 border-zinc-800">
      <CardContent className="p-4 space-y-3">
        <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 flex items-center gap-2">
          <Crosshair className="h-3 w-3" />
          Run a research batch
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end gap-2">
          <div className="flex-1 min-w-[160px]">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Audience
            </Label>
            <Select value={audience} onValueChange={setAudience}>
              <SelectTrigger className="h-9 bg-black border-zinc-800 text-white" data-testid="research-audience">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIENCES.filter((a) => a.value !== "all").map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-[110px]">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              How many?
            </Label>
            <Input
              type="number"
              min={1}
              max={25}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              className="h-9 bg-black border-zinc-800 text-white"
              data-testid="research-count"
            />
          </div>
          <Button
            onClick={handleRun}
            disabled={running || !audienceProviderOk}
            className="h-9 bg-amber-500 hover:bg-amber-400 text-black font-semibold"
            data-testid="research-run"
          >
            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
            <span className="ml-1.5">Research now</span>
          </Button>
        </div>
        {!audienceProviderOk && (
          <div className="text-[11px] text-amber-300/90 flex items-start gap-1.5">
            <AlertCircle className="h-3 w-3 mt-0.5 shrink-0" />
            <span>
              {audience === "residential"
                ? "Regrid must be live to research residential prospects."
                : "B2B research needs at least one source live (Apollo or Perplexity B2B). Both are currently down."}
            </span>
          </div>
        )}
        {audienceProviderOk && audience !== "residential" && (
          <div className="text-[11px] text-zinc-500 flex items-start gap-1.5">
            <CheckCircle2 className="h-3 w-3 mt-0.5 shrink-0 text-emerald-400" />
            <span>
              Dual-source B2B:&nbsp;
              <span className={providers.apollo?.ok ? "text-emerald-300" : "text-zinc-500 line-through"}>
                Apollo
              </span>
              &nbsp;+&nbsp;
              <span className={providers.perplexity_b2b?.ok ? "text-emerald-300" : "text-zinc-500 line-through"}>
                Perplexity
              </span>
              . Results dedup by company name; live sources merge automatically.
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function BulkPromoteDialog({ open, onOpenChange, selected, onCompleted }) {
  const [sequenceOverride, setSequenceOverride] = useState("");
  const [throttle, setThrottle] = useState(0);
  const [stopOnFirstError, setStopOnFirstError] = useState(false);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  useEffect(() => {
    if (open) {
      setReport(null);
      setRunning(false);
    }
  }, [open]);

  const ids = useMemo(() => selected.map((p) => p.id), [selected]);
  const audiences = useMemo(() => {
    const set = new Set(selected.map((p) => p.audience).filter(Boolean));
    return Array.from(set);
  }, [selected]);

  async function runBulk() {
    if (ids.length === 0) return;
    setRunning(true);
    setReport(null);
    try {
      const res = await crmApi.bulkPromoteProspects({
        prospect_ids: ids,
        sequence_slug: sequenceOverride || undefined,
        throttle_ms: Number(throttle) || 0,
        stop_on_first_error: stopOnFirstError,
      });
      setReport(res);
      const msg = `Bulk promote: ${res.succeeded || 0} ok · ${res.failed || 0} blocked · ${res.skipped || 0} skipped`;
      if ((res.succeeded || 0) > 0) toast.success(msg);
      else toast.warning(msg);
      onCompleted?.(res);
    } catch (err) {
      toast.error(err.message || "Bulk promote failed");
    } finally {
      setRunning(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="dark max-w-[720px] bg-black border-zinc-800 text-white overflow-y-auto max-h-[90vh]"
        data-testid="bulk-promote-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-white">
            Bulk promote ({ids.length} prospects)
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Dana runs the CAN-SPAM + suppression gate on each prospect individually. Blocked ones are reported,
            valid ones still enroll. No outreach goes out until enrollment ticks.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 text-[12.5px]">
            <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-1">
              Selection
            </div>
            <div className="text-zinc-200">
              {ids.length} prospect{ids.length === 1 ? "" : "s"}
              {audiences.length > 0 ? (
                <span className="text-zinc-400"> · audiences: {audiences.join(", ")}</span>
              ) : null}
            </div>
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Sequence override (optional — defaults to per-audience)
            </Label>
            <Select value={sequenceOverride || "__auto__"} onValueChange={(v) => setSequenceOverride(v === "__auto__" ? "" : v)}>
              <SelectTrigger className="h-9 bg-zinc-950 border-zinc-800 text-white" data-testid="bulk-sequence-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__auto__">Auto (use each prospect&apos;s audience default)</SelectItem>
                {SEQUENCE_OPTS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
                Throttle between sends (ms)
              </Label>
              <Input
                type="number"
                min={0}
                max={5000}
                value={throttle}
                onChange={(e) => setThrottle(e.target.value)}
                className="h-9 bg-zinc-950 border-zinc-800 text-white"
                data-testid="bulk-throttle-input"
              />
              <div className="text-[10.5px] text-zinc-600 mt-1">0 = no delay. ~500ms recommended for large batches.</div>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
                Error handling
              </Label>
              <button
                type="button"
                onClick={() => setStopOnFirstError((v) => !v)}
                className={`h-9 w-full px-3 rounded border text-[12.5px] flex items-center justify-between transition-colors ${
                  stopOnFirstError
                    ? "border-amber-500/40 bg-amber-500/10 text-amber-200"
                    : "border-zinc-800 bg-zinc-950 text-zinc-300"
                }`}
                data-testid="bulk-stop-on-error-toggle"
              >
                <span>{stopOnFirstError ? "Stop on first failure" : "Continue past failures"}</span>
                {stopOnFirstError ? <CheckSquare className="h-4 w-4" /> : <Square className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {report ? <BulkReportPanel report={report} /> : null}
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={running}
            className="border-zinc-700 text-white hover:bg-zinc-900"
          >
            {report ? "Close" : "Cancel"}
          </Button>
          {!report || (report?.failed || 0) > 0 ? (
            <Button
              type="button"
              onClick={runBulk}
              disabled={running || ids.length === 0}
              className="bg-violet-500 hover:bg-violet-400 text-black font-semibold"
              data-testid="bulk-promote-run"
            >
              {running ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Zap className="h-4 w-4 mr-1.5" />}
              {report ? `Retry (${report.failed} blocked)` : `Run bulk promote (${ids.length})`}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkReportPanel({ report }) {
  if (!report) return null;
  const items = report.items || [];
  const ok = items.filter((i) => i.ok);
  const failed = items.filter((i) => !i.ok);
  return (
    <div className="space-y-3 mt-2" data-testid="bulk-promote-report">
      <div className="grid grid-cols-4 gap-2 text-[12px]">
        <div className="rounded-md border border-emerald-500/40 bg-emerald-500/5 p-2.5 text-center">
          <div className="text-[10px] uppercase tracking-[0.14em] text-emerald-200/80">Succeeded</div>
          <div className="text-emerald-200 text-2xl font-semibold mt-0.5">{report.succeeded}</div>
        </div>
        <div className="rounded-md border border-amber-500/40 bg-amber-500/5 p-2.5 text-center">
          <div className="text-[10px] uppercase tracking-[0.14em] text-amber-200/80">Blocked</div>
          <div className="text-amber-200 text-2xl font-semibold mt-0.5">{report.failed}</div>
        </div>
        <div className="rounded-md border border-zinc-700 bg-zinc-900/30 p-2.5 text-center">
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Skipped</div>
          <div className="text-zinc-300 text-2xl font-semibold mt-0.5">{report.skipped}</div>
        </div>
        <div className="rounded-md border border-zinc-800 bg-zinc-950 p-2.5 text-center">
          <div className="text-[10px] uppercase tracking-[0.14em] text-zinc-500">Elapsed</div>
          <div className="text-white text-lg font-semibold mt-0.5">{report.elapsed_ms ?? 0}ms</div>
        </div>
      </div>

      {ok.length > 0 ? (
        <details className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-2.5" open>
          <summary className="text-[11px] uppercase tracking-[0.16em] text-emerald-200 cursor-pointer">
            Promoted ({ok.length})
          </summary>
          <div className="mt-2 max-h-[180px] overflow-y-auto space-y-1.5">
            {ok.map((it) => (
              <div key={it.prospect_id} className="text-[12.5px] flex items-start gap-2">
                <CheckCircle2 className="h-3.5 w-3.5 mt-0.5 text-emerald-300 shrink-0" />
                <div className="min-w-0">
                  <div className="text-zinc-100 truncate">{it.label || it.prospect_id}</div>
                  <div className="text-[10.5px] text-zinc-500">→ {it.sequence_slug}</div>
                </div>
              </div>
            ))}
          </div>
        </details>
      ) : null}

      {failed.length > 0 ? (
        <details className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5" open>
          <summary className="text-[11px] uppercase tracking-[0.16em] text-amber-200 cursor-pointer">
            Blocked / failed ({failed.length})
          </summary>
          <div className="mt-2 max-h-[240px] overflow-y-auto space-y-2">
            {failed.map((it) => (
              <div key={it.prospect_id} className="text-[12.5px]" data-testid={`bulk-report-blocked-${it.prospect_id}`}>
                <div className="flex items-start gap-2">
                  <XCircle className="h-3.5 w-3.5 mt-0.5 text-amber-300 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-zinc-100 truncate">{it.label || it.prospect_id}</div>
                    <div className="text-[10.5px] text-amber-200/90">{it.reason}</div>
                    {it.suppressed ? (
                      <Badge variant="outline" className="mt-1 border-red-500/40 text-red-300 text-[9.5px]">
                        On suppression list
                      </Badge>
                    ) : null}
                    {(it.violations || []).slice(0, 4).map((v, idx) => (
                      <div key={idx} className="text-[10.5px] text-red-300/90 mt-0.5">
                        · {v}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </details>
      ) : null}
    </div>
  );
}

function BulkActionsBar({ count, onClear, onBulkPromote, onBulkSuppress, suppressBusy }) {
  if (count === 0) return null;
  return (
    <div
      className="sticky top-0 z-10 rounded-md border border-amber-500/40 bg-amber-500/10 backdrop-blur px-3 py-2 flex flex-wrap items-center gap-2"
      data-testid="bulk-actions-bar"
    >
      <div className="text-[12.5px] text-amber-100">
        <span className="font-semibold">{count}</span> selected
      </div>
      <div className="flex-1" />
      <Button
        size="sm"
        onClick={onBulkPromote}
        className="bg-violet-500 hover:bg-violet-400 text-black font-semibold h-8"
        data-testid="bulk-promote-open"
      >
        <Zap className="h-3.5 w-3.5 mr-1.5" />
        Bulk promote
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={onBulkSuppress}
        disabled={suppressBusy}
        className="border-zinc-700 text-zinc-200 hover:bg-zinc-900 h-8"
        data-testid="bulk-suppress-run"
      >
        {suppressBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <ShieldX className="h-3.5 w-3.5 mr-1.5" />}
        Bulk suppress
      </Button>
      <Button
        size="sm"
        variant="ghost"
        onClick={onClear}
        className="text-zinc-300 hover:text-white h-8"
      >
        Clear
      </Button>
    </div>
  );
}

export default function ProspectsView() {
  const [prospects, setProspects] = useState([]);
  const [summary, setSummary] = useState(null);
  const [providerStatus, setProviderStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [audienceFilter, setAudienceFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openId, setOpenId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkPromoteOpen, setBulkPromoteOpen] = useState(false);
  const [bulkSuppressBusy, setBulkSuppressBusy] = useState(false);
  const [bulkReport, setBulkReport] = useState(null);

  const reload = useCallback(
    async (refreshOnly = false) => {
      if (refreshOnly) setRefreshing(true);
      else setLoading(true);
      setError("");
      try {
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (audienceFilter !== "all") params.audience = audienceFilter;
        if (search.trim()) params.search = search.trim();
        const [list, sum] = await Promise.all([
          crmApi.listProspects(params),
          crmApi.prospectsSummary(),
        ]);
        setProspects(list.prospects || []);
        setSummary(sum);
      } catch (err) {
        setError(err.message || "Failed to load prospects");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [statusFilter, audienceFilter, search],
  );

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const params = {};
        if (statusFilter !== "all") params.status = statusFilter;
        if (audienceFilter !== "all") params.audience = audienceFilter;
        if (search.trim()) params.search = search.trim();
        const [list, sum, probe] = await Promise.all([
          crmApi.listProspects(params),
          crmApi.prospectsSummary(),
          crmApi.prospectsProbe().catch(() => null),
        ]);
        if (!active) return;
        setProspects(list.prospects || []);
        setSummary(sum);
        setProviderStatus(probe);
      } catch (err) {
        if (active) setError(err.message || "Failed to load prospects");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
    // We intentionally re-run only when the explicit filters change.
     
  }, [statusFilter, audienceFilter]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    reload(true);
  }

  async function rerunProbe() {
    try {
      const probe = await crmApi.prospectsProbe();
      setProviderStatus(probe);
      toast.success("Provider status refreshed");
    } catch (err) {
      toast.error(err.message || "Probe failed");
    }
  }

  const selectedList = useMemo(
    () => prospects.filter((p) => selectedIds.has(p.id)),
    [prospects, selectedIds],
  );

  const promotablePool = useMemo(
    () => prospects.filter((p) => !["promoted", "responded", "converted", "suppressed"].includes(p.status)),
    [prospects],
  );

  function toggleRow(id) {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAllVisible() {
    const allIds = promotablePool.map((p) => p.id);
    setSelectedIds((cur) => {
      const allSelected = allIds.length > 0 && allIds.every((id) => cur.has(id));
      const next = new Set(cur);
      if (allSelected) {
        allIds.forEach((id) => next.delete(id));
      } else {
        allIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }

  async function handleBulkSuppress() {
    if (selectedIds.size === 0) return;
    setBulkSuppressBusy(true);
    try {
      const res = await crmApi.bulkSuppressProspects({
        prospect_ids: Array.from(selectedIds),
        reason: "manual",
        notes: "Bulk suppressed via Prospects UI",
      });
      const msg = `Bulk suppress: ${res.succeeded || 0} ok · ${res.failed || 0} failed`;
      if ((res.succeeded || 0) > 0) toast.success(msg);
      else toast.warning(msg);
      setSelectedIds(new Set());
      reload(true);
    } catch (err) {
      toast.error(err.message || "Bulk suppress failed");
    } finally {
      setBulkSuppressBusy(false);
    }
  }

  function handleBulkPromoteCompleted(report) {
    setBulkReport(report);
    // Only clear successfully promoted ones from selection
    const successIds = new Set((report?.items || []).filter((i) => i.ok).map((i) => i.prospect_id));
    if (successIds.size > 0) {
      setSelectedIds((cur) => {
        const next = new Set(cur);
        successIds.forEach((id) => next.delete(id));
        return next;
      });
    }
    reload(true);
  }

  const allVisibleChecked =
    promotablePool.length > 0 && promotablePool.every((p) => selectedIds.has(p.id));

  const kpis = useMemo(() => {
    if (!summary) return [];
    return [
      { label: "Total", value: summary.total },
      { label: "Researched", value: summary.by_status?.researched ?? 0 },
      { label: "Promoted", value: summary.by_status?.promoted ?? 0 },
      { label: "Responded", value: summary.by_status?.responded ?? 0 },
      { label: "Converted", value: summary.by_status?.converted ?? 0 },
    ];
  }, [summary]);

  return (
    <div className="space-y-6" data-testid="owner-prospects-view">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold text-white">
            <Target className="h-5 w-5 text-amber-400" />
            Cold Prospects
          </h1>
          <p className="text-sm text-zinc-400">
            Alex Rivera researches Austin-area homeowners, HOAs, realtors, and investors. Drafts a personalized
            cold-email + call script + Dana-compliance checklist. <span className="text-amber-300">Nothing
            sends until you Promote.</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => reload(true)}
            disabled={refreshing}
            className="border-zinc-800 text-white hover:bg-zinc-900"
            data-testid="prospects-refresh"
          >
            {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Refresh</span>
          </Button>
        </div>
      </div>

      {/* Provider banner */}
      <Card className="bg-zinc-950 border-zinc-800">
        <CardContent className="p-3 flex items-center gap-3 flex-wrap">
          <div className="text-[11px] uppercase tracking-[0.16em] text-zinc-500">
            Research providers
          </div>
          <ProviderPill name="Regrid" status={providerStatus?.providers?.regrid} />
          <ProviderPill name="Perplexity" status={providerStatus?.providers?.perplexity} />
          <ProviderPill name="Apollo" status={providerStatus?.providers?.apollo} />
          <ProviderPill name="Perplexity B2B" status={providerStatus?.providers?.perplexity_b2b} />
          <Button
            size="sm"
            variant="ghost"
            onClick={rerunProbe}
            className="ml-auto h-7 text-zinc-300 hover:text-white"
            data-testid="probe-rerun"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Re-check
          </Button>
        </CardContent>
      </Card>

      {/* Run research panel */}
      <ResearchPanel providerStatus={providerStatus} onCompleted={() => reload(true)} />

      {/* Phase E.3 — 24/7 Autonomous Prospector */}
      <ProspectorPanel />

      {/* Phase E2.2 — Suppression list */}
      <SuppressionsPanel refreshKey={prospects.length} />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {kpis.map((tile) => (
          <Card key={tile.label} className="bg-zinc-950 border-zinc-800">
            <CardContent className="p-3">
              <div className="text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                {tile.label}
              </div>
              <div className="text-2xl font-semibold text-white">{tile.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
        <form onSubmit={handleSearchSubmit} className="flex-1 flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
              Search
            </Label>
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-zinc-500" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="org name, address, contact, zip…"
                className="h-9 pl-8 bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600"
                data-testid="prospects-search-input"
              />
            </div>
          </div>
          <Button type="submit" className="h-9 bg-amber-500 hover:bg-amber-400 text-black">
            Search
          </Button>
        </form>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
            Audience
          </Label>
          <Select value={audienceFilter} onValueChange={setAudienceFilter}>
            <SelectTrigger className="h-9 w-[180px] bg-zinc-950 border-zinc-800 text-white" data-testid="prospects-audience-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUDIENCES.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-[0.16em] text-zinc-500 mb-1.5 block">
            Status
          </Label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-[160px] bg-zinc-950 border-zinc-800 text-white" data-testid="prospects-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_OPTS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Table / list */}
      <BulkActionsBar
        count={selectedIds.size}
        onClear={() => setSelectedIds(new Set())}
        onBulkPromote={() => setBulkPromoteOpen(true)}
        onBulkSuppress={handleBulkSuppress}
        suppressBusy={bulkSuppressBusy}
      />

      <div className="rounded-md border border-zinc-800 bg-zinc-950 overflow-hidden">
        <div className="grid grid-cols-[40px_repeat(12,1fr)] text-[10px] uppercase tracking-[0.16em] text-zinc-500 px-4 py-2 border-b border-zinc-800 items-center">
          <div className="flex items-center justify-center">
            <button
              type="button"
              onClick={toggleAllVisible}
              disabled={promotablePool.length === 0}
              className="h-4 w-4 inline-flex items-center justify-center rounded border border-zinc-700 hover:border-amber-400 disabled:opacity-40"
              data-testid="prospects-select-all"
              aria-label={allVisibleChecked ? "Deselect all" : "Select all"}
            >
              {allVisibleChecked ? (
                <CheckSquare className="h-3.5 w-3.5 text-amber-300" />
              ) : (
                <Square className="h-3.5 w-3.5 text-zinc-500" />
              )}
            </button>
          </div>
          <div className="col-span-4">Prospect</div>
          <div className="col-span-2">Audience</div>
          <div className="col-span-2">Recommended</div>
          <div className="col-span-1 text-right">Score</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">When</div>
        </div>
        {loading && (
          <div className="flex items-center justify-center py-12 text-zinc-500">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading prospects…
          </div>
        )}
        {!loading && error && (
          <div className="px-4 py-6 text-red-300 text-[13px]">{error}</div>
        )}
        {!loading && !error && prospects.length === 0 && (
          <div className="px-4 py-12 text-center" data-testid="prospects-empty">
            <Sparkles className="h-6 w-6 text-amber-400 mx-auto mb-2" />
            <div className="text-white text-sm font-medium">No prospects yet</div>
            <div className="text-zinc-500 text-[12px] mt-1">
              Run a research batch above to populate this list.
            </div>
          </div>
        )}
        {!loading && !error && prospects.map((p) => {
          const isSelectable = !["promoted", "responded", "converted", "suppressed"].includes(p.status);
          const checked = selectedIds.has(p.id);
          return (
            <div
              key={p.id}
              data-testid={`prospect-row-${p.id}`}
              className={`grid grid-cols-[40px_repeat(12,1fr)] items-center px-4 py-3 border-b border-zinc-900 text-left transition-colors ${
                checked ? "bg-amber-500/5" : "hover:bg-zinc-900/60"
              }`}
            >
              <div className="flex items-center justify-center">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isSelectable) toggleRow(p.id);
                  }}
                  disabled={!isSelectable}
                  className="h-4 w-4 inline-flex items-center justify-center rounded border border-zinc-700 hover:border-amber-400 disabled:opacity-40"
                  data-testid={`prospect-checkbox-${p.id}`}
                  aria-label={checked ? "Deselect" : "Select"}
                >
                  {checked ? (
                    <CheckSquare className="h-3.5 w-3.5 text-amber-300" />
                  ) : (
                    <Square className="h-3.5 w-3.5 text-zinc-500" />
                  )}
                </button>
              </div>
              <button
                type="button"
                onClick={() => setOpenId(p.id)}
                className="col-span-12 grid grid-cols-12 items-center text-left hover:bg-transparent w-full"
                data-testid={`prospect-row-button-${p.id}`}
              >
                <div className="col-span-4 min-w-0">
                  <div className="text-white font-medium truncate">
                    {p.org_name || p.address || "(unnamed)"}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">
                    {p.research_summary?.slice(0, 80) || ""}
                  </div>
                </div>
                <div className="col-span-2">
                  <Badge variant="outline" className="border-zinc-700 text-zinc-300 text-[10px]">
                    {AUDIENCE_LABEL[p.audience] || p.audience}
                  </Badge>
                </div>
                <div className="col-span-2 text-[11.5px] text-zinc-400 truncate">
                  {p.recommended_offer || "—"}
                </div>
                <div className="col-span-1 text-right">
                  <span className={`text-[13px] font-semibold ${p.score >= 70 ? "text-emerald-300" : p.score >= 50 ? "text-amber-300" : "text-zinc-400"}`}>
                    {p.score}
                  </span>
                </div>
                <div className="col-span-2">
                  <StatusBadge status={p.status} />
                </div>
                <div className="col-span-1 text-right text-[11px] text-zinc-500">
                  {p.created_at ? new Date(p.created_at).toLocaleDateString(undefined, {month:"short",day:"numeric"}) : "—"}
                </div>
              </button>
            </div>
          );
        })}
      </div>

      <ProspectDetailDrawer
        open={Boolean(openId)}
        prospectId={openId}
        onClose={() => setOpenId(null)}
        onUpdated={() => reload(true)}
      />

      <BulkPromoteDialog
        open={bulkPromoteOpen}
        onOpenChange={setBulkPromoteOpen}
        selected={selectedList}
        onCompleted={handleBulkPromoteCompleted}
      />
    </div>
  );
}
